import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/no-exam-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Eligibility Checker Data ───────────────────────────────────────────── */

type AgeGroup = 'under40' | '40to49' | '50to59' | '60to69' | '70plus';
type CoverageAmount = '25k' | '50k' | '100k' | '250k' | '500k' | '1m';
type HealthSituation = 'excellent' | 'good' | 'fair' | 'serious';

interface NoExamProduct {
  name: string;
  carrier: string;
  maxCoverage: string;
  approvalTime: string;
  amBest: string;
  type: string;
  monthlyFrom: string;
  badge?: string;
  features: string[];
  eligible: boolean;
  reason?: string;
}

function getEligibleProducts(
  age: AgeGroup,
  coverage: CoverageAmount,
  health: HealthSituation
): NoExamProduct[] {
  const coverageNum: Record<CoverageAmount, number> = {
    '25k': 25000, '50k': 50000, '100k': 100000,
    '250k': 250000, '500k': 500000, '1m': 1000000,
  };
  const ageNum: Record<AgeGroup, number> = {
    under40: 35, '40to49': 45, '50to59': 55, '60to69': 65, '70plus': 72,
  };
  const cv = coverageNum[coverage];
  const ag = ageNum[age];

  const products: NoExamProduct[] = [
    {
      name: 'Accelerated Underwriting Term',
      carrier: 'Banner Life',
      maxCoverage: '$1,000,000',
      approvalTime: '24–48 hours',
      amBest: 'A+',
      type: 'Term Life (10, 20, 30-yr)',
      monthlyFrom: '$18',
      badge: 'Best Value',
      features: ['No blood draw or urine sample', 'Up to $1M coverage', 'Preferred Plus rates available', 'Instant decision for most applicants'],
      eligible: health !== 'serious' && ag <= 60 && cv <= 1000000,
      reason: health === 'serious' ? 'Requires good to excellent health' : ag > 60 ? 'Available up to age 60' : cv > 1000000 ? 'Max coverage $1M' : undefined,
    },
    {
      name: 'Simplified Issue Term Life',
      carrier: 'Transamerica',
      maxCoverage: '$500,000',
      approvalTime: '24 hours',
      amBest: 'A',
      type: 'Term Life (10, 20-yr)',
      monthlyFrom: '$22',
      badge: 'Most Popular',
      features: ['No medical exam — health questions only', 'Up to $500K coverage', 'Available with some health conditions', 'Same-day approval common'],
      eligible: health !== 'serious' && ag <= 65 && cv <= 500000,
      reason: health === 'serious' ? 'Requires fair to excellent health' : ag > 65 ? 'Available up to age 65' : cv > 500000 ? 'Max coverage $500K' : undefined,
    },
    {
      name: 'Simplified Issue Whole Life',
      carrier: 'Mutual of Omaha',
      maxCoverage: '$300,000',
      approvalTime: '24–72 hours',
      amBest: 'A+',
      type: 'Whole Life (Permanent)',
      monthlyFrom: '$45',
      features: ['No medical exam required', 'Permanent coverage — never expires', 'Builds cash value over time', 'Available with most health conditions'],
      eligible: health !== 'serious' && ag <= 70 && cv <= 300000,
      reason: health === 'serious' ? 'Requires fair to excellent health' : ag > 70 ? 'Available up to age 70' : cv > 300000 ? 'Max coverage $300K' : undefined,
    },
    {
      name: 'Guaranteed Issue Whole Life',
      carrier: 'Gerber Life / AIG',
      maxCoverage: '$25,000',
      approvalTime: 'Instant — no questions',
      amBest: 'A',
      type: 'Whole Life (Permanent)',
      monthlyFrom: '$30',
      features: ['No health questions — guaranteed approval', 'Available ages 50–85', 'No medical exam, no interview', '2-year graded benefit period'],
      eligible: ag >= 50 && cv <= 25000,
      reason: ag < 50 ? 'Available ages 50–85' : cv > 25000 ? 'Max coverage $25K — designed for final expense' : undefined,
    },
    {
      name: 'Final Expense Simplified Issue',
      carrier: 'North American Company',
      maxCoverage: '$50,000',
      approvalTime: '24–48 hours',
      amBest: 'A+',
      type: 'Whole Life (Permanent)',
      monthlyFrom: '$30',
      features: ['No medical exam — 10 health questions', 'Available with most health conditions', 'Permanent coverage — never expires', 'Ideal for seniors ages 50–80'],
      eligible: ag >= 50 && ag <= 80 && cv <= 50000,
      reason: ag < 50 ? 'Designed for ages 50–80' : ag > 80 ? 'Available up to age 80' : cv > 50000 ? 'Max coverage $50K' : undefined,
    },
  ];

  return products;
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is no medical exam life insurance in Indianapolis?',
    a: 'No medical exam life insurance in Indianapolis is a policy that does not require a blood draw, urine sample, or physical examination to qualify. Instead, carriers use one of two methods: (1) Simplified Issue — you answer 5–15 health questions online or by phone, and the carrier uses prescription database checks and MIB records to verify your answers. Approval typically takes 24–48 hours. (2) Guaranteed Issue — no health questions at all, guaranteed approval for anyone in the eligible age range (usually 50–85). No-exam life insurance is available in Indianapolis for coverage amounts up to $1,000,000 depending on the carrier and your age.',
  },
  {
    q: 'How much does no medical exam life insurance cost in Indianapolis?',
    a: 'No medical exam life insurance in Indianapolis typically costs 10–20% more than fully underwritten policies (which require a medical exam). For a healthy 35-year-old Indianapolis resident, simplified issue term life insurance costs approximately $22–$28/month for $250,000 of 20-year term coverage — compared to $19–$22/month for a fully underwritten policy. For seniors, guaranteed issue whole life insurance starts at $30–$45/month for $10,000–$15,000 of coverage. The convenience of no-exam approval (24–48 hours vs. 4–6 weeks for traditional underwriting) is worth the modest premium difference for many Indianapolis residents.',
  },
  {
    q: 'Can I get $500,000 of no medical exam life insurance in Indianapolis?',
    a: 'Yes — $500,000 of no medical exam life insurance is available in Indianapolis through carriers like Transamerica and Banner Life. Transamerica\'s simplified issue term life offers up to $500,000 with no medical exam — just health questions answered online or by phone. Banner Life\'s accelerated underwriting program offers up to $1,000,000 with no exam for applicants under age 60 in good health. Approval typically takes 24–48 hours. For coverage above $500,000, most carriers require a medical exam, though some accelerated underwriting programs extend to $1M for younger, healthy applicants.',
  },
  {
    q: 'How fast can I get approved for no medical exam life insurance in Indianapolis?',
    a: 'No medical exam life insurance approval in Indianapolis is significantly faster than traditional underwriting. Simplified issue policies typically approve in 24–48 hours — some carriers offer same-day decisions. Guaranteed issue policies approve instantly — there are no health questions, so there\'s nothing to underwrite. Compare this to traditional fully underwritten policies, which require scheduling a paramedical exam, waiting for lab results, and underwriter review — a process that typically takes 4–6 weeks. If you need coverage quickly (new baby, new mortgage, business requirement), no-exam life insurance is the fastest path to coverage in Indianapolis.',
  },
  {
    q: 'What health conditions qualify for no medical exam life insurance in Indianapolis?',
    a: 'Many health conditions qualify for no medical exam life insurance in Indianapolis. Simplified issue policies typically accept applicants with: controlled high blood pressure, controlled type 2 diabetes, high cholesterol (controlled), mild asthma, history of anxiety or depression (treated), and many other common conditions. Conditions that may disqualify you from simplified issue (but still qualify for guaranteed issue): active cancer treatment, recent heart attack or stroke (within 2 years), kidney failure requiring dialysis, HIV/AIDS, and terminal illness. Guaranteed issue whole life insurance accepts everyone ages 50–85 regardless of health — no questions asked. An independent agent can match your specific health profile to the right carrier.',
  },
  {
    q: 'Is no medical exam life insurance worth it in Indianapolis?',
    a: 'No medical exam life insurance is worth it in Indianapolis for several situations: (1) You need coverage fast — new baby, new mortgage, or business requirement. (2) You have a fear of needles or medical procedures. (3) You have a busy schedule and can\'t take time off for a paramedical exam. (4) You have health conditions that make traditional underwriting uncertain. (5) You\'re a senior seeking final expense coverage. The 10–20% premium difference is a reasonable trade-off for the speed and convenience. However, if you\'re young, healthy, and not in a hurry, a fully underwritten policy will give you the lowest possible rate.',
  },
  {
    q: 'What is the maximum coverage for no medical exam life insurance in Indianapolis?',
    a: 'The maximum coverage for no medical exam life insurance in Indianapolis depends on the carrier and program: Banner Life accelerated underwriting: up to $1,000,000 (ages 18–60, good health). Transamerica simplified issue: up to $500,000 (ages 18–65). Mutual of Omaha simplified issue whole life: up to $300,000. North American Company final expense: up to $50,000. Gerber Life / AIG guaranteed issue: up to $25,000. For most Indianapolis families, $250,000–$500,000 of no-exam term life insurance covers the mortgage and income replacement needs. Seniors typically need $10,000–$25,000 of final expense coverage, which is easily obtained with no exam.',
  },
  {
    q: 'Do I need a medical exam for term life insurance in Indianapolis?',
    a: 'Not necessarily. Traditional term life insurance in Indianapolis requires a medical exam (blood draw, urine sample, blood pressure check) for coverage above $100,000. However, accelerated underwriting programs from carriers like Banner Life and Protective Life now offer up to $1,000,000 of term life insurance with no exam for applicants under age 60 in good health. The carrier uses prescription database checks, MIB records, and driving history to underwrite the policy without a physical exam. Approval takes 24–48 hours instead of 4–6 weeks. If you\'re over 60 or have significant health conditions, a medical exam may still be required for term life coverage above $100,000.',
  },
  {
    q: 'What is the difference between simplified issue and guaranteed issue life insurance in Indianapolis?',
    a: 'Simplified issue and guaranteed issue are the two main types of no medical exam life insurance available in Indianapolis. Simplified Issue: Requires answering 5–15 health questions (no medical exam). Carriers check prescription databases and MIB records. Approval in 24–48 hours. Available for coverage up to $500,000–$1,000,000. Costs 10–20% more than fully underwritten policies. Guaranteed Issue: No health questions — guaranteed approval for anyone in the eligible age range (usually 50–85). Instant approval. Maximum coverage typically $25,000. Costs significantly more per dollar of coverage. Has a 2-year graded benefit period (if you die in the first 2 years, beneficiaries receive premiums paid plus interest, not the full death benefit). Simplified issue is better for most Indianapolis residents; guaranteed issue is the last resort for those who can\'t qualify for any other coverage.',
  },
  {
    q: 'Can seniors get no medical exam life insurance in Indianapolis?',
    a: 'Yes — seniors in Indianapolis have excellent no medical exam life insurance options. For seniors ages 50–80 in fair to good health, simplified issue final expense whole life insurance is available from $10,000–$50,000 of coverage with no medical exam. Mutual of Omaha and North American Company are the top carriers for Indianapolis seniors. For seniors ages 50–85 with serious health conditions, guaranteed issue whole life insurance provides $5,000–$25,000 of coverage with no health questions and instant approval. Rates for a 65-year-old Indianapolis senior start at approximately $45–$60/month for $10,000 of guaranteed issue coverage, or $38–$52/month for $15,000 of simplified issue final expense coverage.',
  },
  {
    q: 'How do I apply for no medical exam life insurance in Indianapolis?',
    a: 'Applying for no medical exam life insurance in Indianapolis is simple and fast. Step 1: Call Hoosier Life Insurance at (317) 648-2983 or book a free 15-minute consultation. Step 2: We compare all available no-exam carriers for your age, coverage need, and health situation — simultaneously. Step 3: We submit your application to the best-fit carrier. For simplified issue, you\'ll answer health questions by phone or online. For guaranteed issue, there are no questions. Step 4: Receive your decision — typically 24–48 hours for simplified issue, instant for guaranteed issue. Step 5: Your policy is issued and coverage begins. The entire process from first call to active coverage typically takes 1–3 business days for no-exam policies.',
  },
];

/* ─── Carrier Comparison ─────────────────────────────────────────────────── */

const CARRIERS = [
  {
    name: 'Banner Life',
    amBest: 'A+',
    maxCoverage: '$1,000,000',
    maxAge: '60',
    approvalTime: '24–48 hrs',
    type: 'Accelerated UW Term',
    specialty: 'Best for healthy applicants under 60 wanting maximum no-exam coverage',
    icon: 'ri-shield-star-line',
  },
  {
    name: 'Transamerica',
    amBest: 'A',
    maxCoverage: '$500,000',
    maxAge: '65',
    approvalTime: '24 hrs',
    type: 'Simplified Issue Term',
    specialty: 'Best for applicants with some health conditions — most lenient underwriting',
    icon: 'ri-heart-pulse-line',
  },
  {
    name: 'Mutual of Omaha',
    amBest: 'A+',
    maxCoverage: '$300,000',
    maxAge: '70',
    approvalTime: '24–72 hrs',
    type: 'Simplified Issue Whole Life',
    specialty: 'Best for permanent coverage without an exam — strong senior options',
    icon: 'ri-user-heart-line',
  },
  {
    name: 'North American Company',
    amBest: 'A+',
    maxCoverage: '$50,000',
    maxAge: '80',
    approvalTime: '24–48 hrs',
    type: 'Final Expense Whole Life',
    specialty: 'Best for seniors ages 50–80 seeking final expense coverage',
    icon: 'ri-home-heart-line',
  },
  {
    name: 'Gerber Life / AIG',
    amBest: 'A',
    maxCoverage: '$25,000',
    maxAge: '85',
    approvalTime: 'Instant',
    type: 'Guaranteed Issue Whole Life',
    specialty: 'Best for applicants with serious health conditions — no questions asked',
    icon: 'ri-checkbox-circle-line',
  },
  {
    name: 'Protective Life',
    amBest: 'A+',
    maxCoverage: '$1,000,000',
    maxAge: '60',
    approvalTime: '24–48 hrs',
    type: 'Accelerated UW Term',
    specialty: 'Best for 30-year term no-exam coverage — competitive rates for younger applicants',
    icon: 'ri-calendar-check-line',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'Rashida & Marcus T.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'We needed coverage fast — new baby, new mortgage. Benjamin got us both approved for $500K each in 36 hours. No exam, no doctor visit. Just answered questions online. Couldn\'t believe how easy it was.',
    stars: 5,
    coverage: '$500K each',
    approvalTime: '36 hours',
  },
  {
    name: 'Patricia H.',
    location: 'Warren Township, Indianapolis',
    quote: 'I have high blood pressure and was terrified I\'d be denied. Benjamin found me a simplified issue policy with Transamerica — $250K for $34/month. Approved in 24 hours. No exam, no blood draw.',
    stars: 5,
    coverage: '$250K',
    approvalTime: '24 hours',
  },
  {
    name: 'Donald & Shirley M.',
    location: 'Pike Township, Indianapolis',
    quote: 'We\'re both in our 60s with some health issues. Benjamin found us guaranteed issue whole life — $20K each, no questions asked. Approved instantly. Finally have peace of mind for our final expenses.',
    stars: 5,
    coverage: '$20K each',
    approvalTime: 'Instant',
  },
  {
    name: 'Kevin L.',
    location: 'Greenwood, IN',
    quote: 'Needle phobia — I\'d been putting off life insurance for years because of the blood draw. Benjamin told me about no-exam options. Got $500K of 20-year term approved in 48 hours. No needles, no problem.',
    stars: 5,
    coverage: '$500K',
    approvalTime: '48 hours',
  },
];

/* ─── Process Steps ──────────────────────────────────────────────────────── */

const PROCESS_STEPS = [
  {
    step: '01',
    title: 'Free 15-Minute Consultation',
    desc: 'Call (317) 648-2983 or book online. We\'ll ask about your age, coverage need, and health situation to identify the best no-exam carriers for you.',
    icon: 'ri-phone-line',
    time: '15 minutes',
  },
  {
    step: '02',
    title: 'We Compare All No-Exam Carriers',
    desc: 'We simultaneously compare all available no-exam carriers — Banner Life, Transamerica, Mutual of Omaha, and more — to find your best rate and fastest approval.',
    icon: 'ri-search-line',
    time: '5 minutes',
  },
  {
    step: '03',
    title: 'Answer Health Questions (or None)',
    desc: 'For simplified issue: answer 5–15 health questions online or by phone. For guaranteed issue: no questions at all. No blood draw, no urine sample, no doctor visit.',
    icon: 'ri-questionnaire-line',
    time: '10–15 minutes',
  },
  {
    step: '04',
    title: 'Receive Your Decision',
    desc: 'Simplified issue: decision in 24–48 hours. Guaranteed issue: instant approval. Most Indianapolis applicants are approved same-day or next-day.',
    icon: 'ri-checkbox-circle-line',
    time: '24–48 hours',
  },
  {
    step: '05',
    title: 'Coverage Begins',
    desc: 'Once approved and your first premium is paid, your coverage is active. Your family is protected — no waiting, no exam, no hassle.',
    icon: 'ri-shield-check-line',
    time: 'Immediate',
  },
];

/* ─── Who Needs No-Exam ──────────────────────────────────────────────────── */

const WHO_NEEDS = [
  { icon: 'ri-parent-line', title: 'New Parents', desc: 'Need coverage fast after a new baby — can\'t wait 4–6 weeks for traditional underwriting.' },
  { icon: 'ri-home-4-line', title: 'New Homeowners', desc: 'Just closed on a home and need mortgage protection coverage immediately.' },
  { icon: 'ri-syringe-line', title: 'Needle Phobia', desc: 'Avoid the blood draw and urine sample required by traditional underwriting.' },
  { icon: 'ri-heart-pulse-line', title: 'Health Conditions', desc: 'Controlled conditions like high blood pressure or diabetes — simplified issue often approves.' },
  { icon: 'ri-briefcase-line', title: 'Business Owners', desc: 'Need key person or buy-sell coverage quickly for a business requirement or loan.' },
  { icon: 'ri-user-heart-line', title: 'Seniors 50–85', desc: 'Final expense coverage with no exam — guaranteed issue available regardless of health.' },
  { icon: 'ri-time-line', title: 'Busy Professionals', desc: 'Can\'t take time off work to schedule and attend a paramedical exam appointment.' },
  { icon: 'ri-calendar-close-line', title: 'Previously Declined', desc: 'Denied for traditional coverage — guaranteed issue accepts everyone in the eligible age range.' },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function NoExamLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Eligibility Checker state
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('40to49');
  const [coverageAmount, setCoverageAmount] = useState<CoverageAmount>('250k');
  const [healthSituation, setHealthSituation] = useState<HealthSituation>('good');
  const [checkerResults, setCheckerResults] = useState<NoExamProduct[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'No Medical Exam Life Insurance Indianapolis IN | Approved in 24 Hours | Hoosier Life';
    const description = 'Get no medical exam life insurance in Indianapolis, IN. Approved in 24–48 hours — no blood draw, no doctor visit. Coverage up to $1M. Use our Instant Eligibility Checker. Call (317) 648-2983.';
    const keywords = 'no medical exam life insurance Indianapolis, no exam life insurance Indianapolis, simplified issue life insurance Indianapolis, guaranteed issue life insurance Indianapolis Indiana';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/21b77f6a6afeab72ccfb409dd3464871.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-14T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        speakable: {
          '@type': 'SpeakableSpecification',
          xpath: ['/html/body//*[@id="eligibility-checker"]'],
        },
        about: [
          { '@type': 'Thing', name: 'No Medical Exam Life Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Simplified Issue Life Insurance Indianapolis' },
          { '@type': 'Thing', name: 'Guaranteed Issue Life Insurance Indianapolis Indiana' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'No Medical Exam Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'No Medical Exam Life Insurance',
        name: 'No Medical Exam Life Insurance Indianapolis',
        description: 'No medical exam life insurance for Indianapolis and Marion County residents. Approved in 24–48 hours — no blood draw, no doctor visit. Coverage up to $1M. Independent agent — 20+ carriers compared.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleCheckEligibility = () => {
    const results = getEligibleProducts(ageGroup, coverageAmount, healthSituation);
    setCheckerResults(results);
  };

  const ageOptions: { value: AgeGroup; label: string }[] = [
    { value: 'under40', label: 'Under 40' },
    { value: '40to49', label: 'Ages 40–49' },
    { value: '50to59', label: 'Ages 50–59' },
    { value: '60to69', label: 'Ages 60–69' },
    { value: '70plus', label: 'Ages 70+' },
  ];

  const coverageOptions: { value: CoverageAmount; label: string }[] = [
    { value: '25k', label: '$25,000' },
    { value: '50k', label: '$50,000' },
    { value: '100k', label: '$100,000' },
    { value: '250k', label: '$250,000' },
    { value: '500k', label: '$500,000' },
    { value: '1m', label: '$1,000,000' },
  ];

  const healthOptions: { value: HealthSituation; label: string; desc: string }[] = [
    { value: 'excellent', label: 'Excellent', desc: 'No major conditions' },
    { value: 'good', label: 'Good', desc: 'Controlled conditions (BP, cholesterol)' },
    { value: 'fair', label: 'Fair', desc: 'Managed conditions (diabetes, past cancer)' },
    { value: 'serious', label: 'Serious', desc: 'Active cancer, recent heart attack/stroke' },
  ];

  const eligibleCount = checkerResults ? checkerResults.filter(p => p.eligible).length : 0;

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">No Medical Exam</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[580px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20couple%20in%20their%2040s%20sitting%20together%20at%20modern%20kitchen%20island%20completing%20life%20insurance%20application%20on%20tablet%20no%20medical%20exam%20concept%20warm%20contemporary%20home%20environment%20Marion%20County%20Indiana%20natural%20light%20relaxed%20confident%20no%20stress%20no%20doctor%20visit%20photorealistic%20warm%20neutral%20tones&width=1440&height=700&seq=noexam-indy-hero-2025a&orientation=landscape"
            alt="No Medical Exam Life Insurance Indianapolis Indiana — Approved in 24 Hours"
            title="No Medical Exam Life Insurance in Indianapolis, Indiana — Instant Eligibility Checker"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-timer-flash-line text-sm"></i>
              Indianapolis &amp; Marion County · No Exam · Approved in 24–48 Hours
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              No Medical Exam Life Insurance in{' '}
              <span className="text-[#D97642]">Indianapolis, Indiana</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              No blood draw. No doctor visit. No waiting 6 weeks. Indianapolis residents can get up to <strong className="text-white">$1,000,000</strong> of life insurance coverage approved in <strong className="text-white">24–48 hours</strong> — just answer a few health questions online.
            </p>

            {/* Hero Stats Teaser */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '24 hrs', label: 'Typical approval time' },
                { value: '$1M', label: 'Max no-exam coverage' },
                { value: '0', label: 'Blood draws required' },
                { value: '20+', label: 'Carriers compared' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#eligibility-checker"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-checkbox-circle-line text-lg"></i>
                Check My Eligibility
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
              <button
                onClick={() => {
                  const widget = document.querySelector('readdy-agent') as HTMLElement & { open?: () => void };
                  if (widget && typeof widget.open === 'function') {
                    widget.open();
                  } else {
                    const btn = document.querySelector('.readdy-agent-button') as HTMLElement;
                    if (btn) btn.click();
                  }
                }}
                className="inline-flex items-center gap-2 px-6 py-4 bg-white/15 hover:bg-white/25 border border-white/30 hover:border-white/50 text-white text-base font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-chat-3-line text-lg"></i>
                Chat with us
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '24–48 hrs', label: 'Typical approval — vs. 4–6 weeks for traditional underwriting' },
              { value: '$1,000,000', label: 'Maximum no-exam coverage available in Indianapolis' },
              { value: '5', label: 'No-exam carrier options — we compare all of them for you' },
              { value: '$0', label: 'Cost to compare — quotes and eligibility checks are always free' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: No Medical Exam Life Insurance in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'No blood draw, no urine sample, no physical exam — just answer health questions online or by phone',
                  'Coverage up to $1,000,000 available with no exam for Indianapolis residents under age 60 in good health',
                  'Approval in 24–48 hours — vs. 4–6 weeks for traditional fully underwritten policies',
                  'Simplified issue accepts many health conditions: controlled BP, diabetes, high cholesterol, and more',
                  'Guaranteed issue available for ages 50–85 — no health questions, instant approval, up to $25,000',
                  'No-exam policies cost 10–20% more than fully underwritten — a reasonable trade-off for speed and convenience',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* ── ELIGIBILITY CHECKER ───────────────────────────────────── */}
            <div id="eligibility-checker" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Instant Eligibility Checker</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Which No-Exam Policies Can You Get in Indianapolis?
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and health situation to see which no medical exam life insurance products you qualify for — with carrier, approval time, and starting rate.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Controls */}
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age Group */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setAgeGroup(opt.value); setCheckerResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${ageGroup === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage Amount */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount Needed</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setCoverageAmount(opt.value); setCheckerResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${coverageAmount === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health Situation */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Current Health Situation</label>
                    <div className="grid grid-cols-2 gap-2">
                      {healthOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setHealthSituation(opt.value); setCheckerResults(null); }}
                          className={`px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${healthSituation === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${healthSituation === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleCheckEligibility}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base"
                  >
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    Check My No-Exam Eligibility
                  </button>
                </div>

                {/* Results */}
                {checkerResults !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Your No-Exam Options — Indianapolis, IN
                      </h3>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${eligibleCount > 0 ? 'bg-[#4A7C59]/10 text-[#4A7C59]' : 'bg-red-50 text-red-500'}`}>
                        {eligibleCount} of {checkerResults.length} eligible
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-6">Results based on your profile. Actual eligibility determined by full underwriting.</p>

                    <div className="flex flex-col gap-4">
                      {checkerResults.map((product) => (
                        <div
                          key={product.name}
                          className={`rounded-xl border p-5 ${product.eligible ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50 opacity-60'}`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-sm font-bold text-[#1A3A52]">{product.name}</span>
                                {product.badge && product.eligible && (
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${product.badge === 'Most Popular' ? 'bg-[#D97642] text-white' : 'bg-[#4A7C59] text-white'}`}>
                                    {product.badge}
                                  </span>
                                )}
                                <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {product.amBest}</span>
                              </div>
                              <p className="text-xs text-gray-500">{product.carrier} · {product.type}</p>
                            </div>
                            <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${product.eligible ? 'bg-[#4A7C59] text-white' : 'bg-gray-200 text-gray-400'}`}>
                              <i className={`${product.eligible ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} text-lg`}></i>
                            </div>
                          </div>

                          {product.eligible ? (
                            <>
                              <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-white rounded-lg p-3 text-center">
                                  <div className="text-xs text-gray-400 mb-1">Max Coverage</div>
                                  <div className="text-sm font-extrabold text-[#1A3A52]">{product.maxCoverage}</div>
                                </div>
                                <div className="bg-white rounded-lg p-3 text-center">
                                  <div className="text-xs text-gray-400 mb-1">Approval Time</div>
                                  <div className="text-sm font-extrabold text-[#D97642]">{product.approvalTime}</div>
                                </div>
                                <div className="bg-white rounded-lg p-3 text-center">
                                  <div className="text-xs text-gray-400 mb-1">Starting At</div>
                                  <div className="text-sm font-extrabold text-[#4A7C59]">{product.monthlyFrom}/mo</div>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {product.features.map(f => (
                                  <span key={f} className="text-xs bg-white text-gray-600 border border-gray-200 px-2 py-1 rounded-full flex items-center gap-1">
                                    <i className="ri-check-line text-[#4A7C59]"></i>
                                    {f}
                                  </span>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <i className="ri-information-line text-gray-300"></i>
                              <span>{product.reason}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {eligibleCount === 0 && (
                      <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-start gap-3">
                          <i className="ri-information-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                          <div>
                            <p className="text-sm font-semibold text-amber-800 mb-1">Let's find your options</p>
                            <p className="text-xs text-amber-700">Based on your profile, standard no-exam products may not be the best fit. Call us — there may be options we can find through full underwriting or specialized carriers.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-5 p-4 bg-[#1A3A52]/5 rounded-xl">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Eligibility shown is an estimate based on general underwriting guidelines. Actual eligibility is determined by the carrier after full application review. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized assessment.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                    >
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact No-Exam Quote — Free 15-Min Call
                    </button>
                  </div>
                )}

                {checkerResults === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-checkbox-circle-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage amount, and health situation above, then click <strong className="text-[#1A3A52]">Check My No-Exam Eligibility</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Who Needs No-Exam */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Is This Right for You?</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                8 Reasons Indianapolis Residents Choose No Medical Exam Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {WHO_NEEDS.map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-xl text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-sm font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Carrier Comparison Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Carrier Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Top 6 No Medical Exam Life Insurance Carriers in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                We compare all available no-exam carriers simultaneously to find your best rate and fastest approval. Here's how they stack up:
              </p>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4">
                  <h3 className="text-sm font-bold text-white">No Medical Exam Life Insurance Carriers — Indianapolis, IN (2026)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Carrier</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">AM Best</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Max Coverage</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Max Age</th>
                        <th className="px-5 py-3 text-center font-semibold text-[#D97642]">Approval Time</th>
                        <th className="px-5 py-3 text-left font-semibold text-gray-600">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {CARRIERS.map((carrier, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                                <i className={`${carrier.icon} text-sm text-[#D97642]`}></i>
                              </div>
                              <span className="font-semibold text-[#1A3A52]">{carrier.name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-2 py-0.5 rounded-full">{carrier.amBest}</span>
                          </td>
                          <td className="px-5 py-4 font-semibold text-[#1A3A52]">{carrier.maxCoverage}</td>
                          <td className="px-5 py-4 text-gray-600">{carrier.maxAge}</td>
                          <td className="px-5 py-4 text-center font-bold text-[#D97642]">{carrier.approvalTime}</td>
                          <td className="px-5 py-4 text-gray-500 text-xs">{carrier.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Carrier information current as of April 2026. Eligibility and rates subject to underwriting. An independent agent compares all carriers simultaneously.</p>
                </div>
              </div>

              {/* Carrier specialty cards */}
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {CARRIERS.map((carrier, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${carrier.icon} text-lg text-[#D97642]`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-bold text-[#1A3A52]">{carrier.name}</span>
                        <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {carrier.amBest}</span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{carrier.specialty}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">The Process</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                How to Get No Medical Exam Life Insurance in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From first call to active coverage in as little as 24 hours. Here's exactly how it works:
              </p>
              <div className="flex flex-col gap-4">
                {PROCESS_STEPS.map((step, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 border border-gray-100 flex items-start gap-5">
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D97642] text-white font-extrabold text-sm">
                        {step.step}
                      </div>
                      {i < PROCESS_STEPS.length - 1 && (
                        <div className="w-0.5 h-6 bg-gray-200"></div>
                      )}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="text-base font-bold text-[#1A3A52]">{step.title}</h3>
                        <span className="text-xs bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                          <i className="ri-time-line text-xs"></i>
                          {step.time}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Simplified vs Guaranteed Comparison */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Know the Difference</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Simplified Issue vs. Guaranteed Issue — Which Is Right for You?
              </h2>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  {
                    type: 'Simplified Issue',
                    icon: 'ri-questionnaire-line',
                    color: '#D97642',
                    tagline: 'Best for most Indianapolis residents',
                    pros: [
                      'Coverage up to $500K–$1M',
                      'Rates 10–20% above fully underwritten',
                      'Approval in 24–48 hours',
                      'Accepts many health conditions',
                      'Available for ages 18–70',
                    ],
                    cons: [
                      'Requires answering 5–15 health questions',
                      'Carrier checks prescription database & MIB',
                      'May be declined for serious conditions',
                    ],
                    bestFor: 'Healthy to moderately healthy applicants who want fast approval without a medical exam',
                  },
                  {
                    type: 'Guaranteed Issue',
                    icon: 'ri-checkbox-circle-line',
                    color: '#4A7C59',
                    tagline: 'Last resort — but always available',
                    pros: [
                      'No health questions — guaranteed approval',
                      'Available for ages 50–85',
                      'Instant approval',
                      'No medical exam, no interview',
                      'Accepts any health condition',
                    ],
                    cons: [
                      'Maximum coverage $25,000',
                      'Significantly higher cost per dollar of coverage',
                      '2-year graded benefit period',
                    ],
                    bestFor: 'Seniors with serious health conditions who cannot qualify for any other coverage',
                  },
                ].map(item => (
                  <div key={item.type} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-100" style={{ backgroundColor: `${item.color}10` }}>
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${item.color}20` }}>
                          <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-[#1A3A52]">{item.type}</h3>
                          <p className="text-xs font-semibold" style={{ color: item.color }}>{item.tagline}</p>
                        </div>
                      </div>
                    </div>
                    <div className="px-6 py-5">
                      <div className="mb-4">
                        <p className="text-xs font-bold text-[#4A7C59] uppercase tracking-wide mb-2">Advantages</p>
                        <ul className="flex flex-col gap-1.5">
                          {item.pros.map(pro => (
                            <li key={pro} className="flex items-start gap-2 text-sm text-gray-600">
                              <i className="ri-check-line text-[#4A7C59] flex-shrink-0 mt-0.5"></i>
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="mb-4">
                        <p className="text-xs font-bold text-red-400 uppercase tracking-wide mb-2">Limitations</p>
                        <ul className="flex flex-col gap-1.5">
                          {item.cons.map(con => (
                            <li key={con} className="flex items-start gap-2 text-sm text-gray-500">
                              <i className="ri-close-line text-red-300 flex-shrink-0 mt-0.5"></i>
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500"><strong className="text-[#1A3A52]">Best for:</strong> {item.bestFor}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Residents Who Got Approved Without a Medical Exam
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                        <i className="ri-timer-flash-line text-xs"></i>
                        {t.approvalTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                No Medical Exam Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-noexam-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky Sidebar */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-timer-flash-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free No-Exam Quote</h3>
                    <p className="text-xs text-white/70">Approved in 24–48 hours · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No blood draw</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No doctor visit</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Up to $1M coverage</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Results in 24–48 hours</p>
              </div>
            </div>

            {/* Quick Approval Timeline */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-timer-flash-line text-[#D97642]"></i>
                Approval Timeline — No Exam vs. Traditional
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Guaranteed Issue', time: 'Instant', color: '#4A7C59' },
                  { label: 'Simplified Issue (Transamerica)', time: '24 hours', color: '#4A7C59' },
                  { label: 'Accelerated UW (Banner Life)', time: '24–48 hours', color: '#D97642' },
                  { label: 'Simplified Issue (Mutual of Omaha)', time: '24–72 hours', color: '#D97642' },
                  { label: 'Traditional Fully Underwritten', time: '4–6 weeks', color: '#9CA3AF' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <p className="text-xs text-gray-600 font-medium">{item.label}</p>
                    <span className="text-xs font-extrabold whitespace-nowrap ml-3" style={{ color: item.color }}>{item.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Quote Call</h3>
                    <p className="text-xs text-white/70">No exam · Fast approval · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your No-Exam Quote Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free No-Exam Quote Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers — Indianapolis', icon: 'ri-search-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis', icon: 'ri-heart-pulse-line' },
                  { to: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis', icon: 'ri-shield-check-line' },
                  { to: '/instant-quote', label: 'Instant Online Quote Tool', icon: 'ri-file-list-3-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20charcoal%20blazer%20and%20white%20shirt%20friendly%20confident%20smile%20sitting%20at%20modern%20desk%20with%20laptop%20showing%20no%20medical%20exam%20life%20insurance%20application%20Indianapolis%20Indiana%20office%20natural%20window%20light%20trustworthy%20approachable%20financial%20advisor%20Marion%20County%20Indiana%20professional%20portrait%20photography%20warm%20tones&width=600&height=700&seq=agent-bwright-noexam-indy-2025a&orientation=portrait"
                alt="Benjamin Wright — No Medical Exam Life Insurance Agent in Indianapolis, Indiana"
                title="Benjamin Wright — No Medical Exam Life Insurance Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-timer-flash-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Approved in</div>
                    <div className="text-base font-bold text-[#1A3A52]">24–48 Hours</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis No-Exam Specialist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                I've helped hundreds of Indianapolis and Marion County residents get life insurance coverage without a medical exam. Whether you're a new parent who needs coverage fast, someone with a health condition who's been declined before, or a senior looking for final expense coverage — I know which carriers will approve you and at what rate.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                As an independent agent, I compare all available no-exam carriers simultaneously — Banner Life, Transamerica, Mutual of Omaha, and more. Most Indianapolis applicants are approved within 24–48 hours. No blood draw, no doctor visit, no waiting.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Get My No-Exam Quote
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore All Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Find the right life insurance for your specific situation in Indianapolis</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/life-insurance-quotes-indianapolis', icon: 'ri-search-line', title: 'Compare Quotes Indianapolis', desc: 'Interactive 6-carrier rate comparison tool — see all options ranked lowest to highest' },
              { to: '/affordable-life-insurance-indianapolis', icon: 'ri-money-dollar-circle-line', title: 'Affordable Life Insurance Indianapolis', desc: 'Budget calculator — see exactly what your monthly budget buys from $15/month' },
              { to: '/final-expense-insurance-indianapolis', icon: 'ri-heart-pulse-line', title: 'Final Expense Insurance Indianapolis', desc: 'Senior coverage from $30/month — no medical exam, no waiting period' },
              { to: '/burial-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Burial Insurance Indianapolis', desc: 'Cover $9K–$15K funeral costs — protect your family from a major unexpected expense' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            No blood draw. No doctor visit. Approved in 24 hours.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Eligibility Checker to see which no-exam policies you qualify for — then call for your exact personalized rate. 20+ A-rated carriers compared. No obligation, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#eligibility-checker"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-checkbox-circle-line text-lg"></i>
              Check My Eligibility
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="No Medical Exam Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="No-Exam Life Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your No Medical Exam Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
